import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import './SentencePracticePage.css';
import { backendUrl } from '../config';
import Spinner from './Spinner';
import { useLanguage } from '../contexts/LanguageContext';
import { getKeyboardLangCode } from '../utils/keyboardUtils';
import { isRTL as isRTLanguage } from '../utils/rtlUtils';
import { supabase } from '../supabaseClient';
import { useModuleData } from '../hooks/useModuleData';
import { useWordGroupData } from '../hooks/useWordGroupData';
import { useWordsData } from '../hooks/useWordsData';
import { cacheUtils } from '../utils/cacheManager';

function getUniqueDistractors(correct, groupWords, count) {
  // Exclude the correct answer and any duplicates
  const distractors = groupWords
    .map(w => w.to_text)
    .filter(word => word !== correct && word !== undefined && word !== null);
  // Shuffle and pick count
  for (let i = distractors.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [distractors[i], distractors[j]] = [distractors[j], distractors[i]];
  }
  return distractors.slice(0, count);
}

// Helper to get MCQ choices for a sentence
function getMCQChoices(sentence, groupWords) {
  const correct = sentence.correct_word;
  const distractors = getUniqueDistractors(correct, groupWords, 3);

  // Normalize casing of all choices to match the correct answer's casing
  const normalizeCasing = (word, targetCasing) => {
    if (!word || !targetCasing) return word;

    // If target is all lowercase, make all choices lowercase
    if (targetCasing === targetCasing.toLowerCase()) {
      return word.toLowerCase();
    }
    // If target is all uppercase, make all choices uppercase
    else if (targetCasing === targetCasing.toUpperCase()) {
      return word.toUpperCase();
    }
    // If target is title case (first letter capitalized), make all choices title case
    else if (targetCasing === targetCasing.charAt(0).toUpperCase() + targetCasing.slice(1).toLowerCase()) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    // Otherwise, keep original casing
    return word;
  };

  // Normalize distractors to match correct answer's casing
  const normalizedDistractors = distractors.map(distractor =>
    normalizeCasing(distractor, correct)
  );

  const allChoices = [correct, ...normalizedDistractors];
  // Shuffle
  for (let i = allChoices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allChoices[i], allChoices[j]] = [allChoices[j], allChoices[i]];
  }
  return allChoices;
}



const MultipleChoicePracticePage = () => {
  const { t, isRTL } = useLanguage();
  const { id, groupId } = useParams();
  const { refetchUsage } = useOutletContext() || {};
  const navigate = useNavigate();

  // Use DataContext hooks
  const { moduleData, loading: moduleLoading } = useModuleData(id);
  const { groupData, loading: groupLoading } = useWordGroupData(id, groupId);
  const { words, loading: wordsLoading } = useWordsData(groupData?.id);

  // Debug logging


  const dataLoading = moduleLoading || groupLoading || wordsLoading;

  const [sentences, setSentences] = useState([]);
  const [choices, setChoices] = useState([]); // Store choices for each sentence
  const [userChoices, setUserChoices] = useState([]); // index of selected choice per question
  const [feedback, setFeedback] = useState([]); // 'correct' or 'incorrect' per question
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);
  const requestIdRef = useRef(0);
  const [showHints, setShowHints] = useState([]); // Track which hints are shown



  const fetchSentences = useCallback(async () => {
    setLoading(true);
    setError('');
    setIsSubmitted(false);
    setSentences([]);
    setChoices([]);
    const currentRequestId = ++requestIdRef.current;
    try {
      const shuffledWords = [...words].sort(() => 0.5 - Math.random());
      const selectedWords = shuffledWords.slice(0, 10);
      // Format words for backend - ensure we send the actual word text, not IDs
      const formattedWords = selectedWords.map(word => ({
        from_text: word.from_text,
        to_text: word.to_text
      }));
      // Get the current user's access token
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      const res = await fetch(`${backendUrl}/gemini-generate-sentences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLang: moduleData.from_lang,
          toLang: moduleData.to_lang,
          words: formattedWords,
          dialect: moduleData.dialect,
          accessToken,
          practiceType: 'mcq'
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        if (requestIdRef.current === currentRequestId) setError(data.error || t('aiCouldNotGenerateSentences'));
        return;
      }
      const data = await res.json();
      if (data.sentences && data.sentences.length > 0) {
        if (requestIdRef.current === currentRequestId) {
          const generatedChoices = data.sentences.map(sentence => getMCQChoices(sentence, words));
          cacheUtils.cacheSentences(moduleData.id, groupData.id, 'mcq', data.sentences);
          setSentences(data.sentences);
          setChoices(generatedChoices);
          setUserChoices(Array(data.sentences.length).fill(null));
          setFeedback(Array(data.sentences.length).fill(null));
          setShowHints(Array(data.sentences.length).fill(false));
          if (refetchUsage) refetchUsage();
        }
      } else {
        if (requestIdRef.current === currentRequestId) setError(data.error || 'The AI could not generate sentences for this word group. Please try another one.');
      }
    } catch (error) {
      if (requestIdRef.current === currentRequestId) setError(t('couldNotConnectToAI'));
    } finally {
      if (requestIdRef.current === currentRequestId) setLoading(false);
    }
  }, [words, moduleData?.from_lang, moduleData?.to_lang, moduleData?.dialect, moduleData?.id, groupData?.id, t, refetchUsage]);

  useEffect(() => {
    if (!hasFetched.current && words && words.length > 0 && moduleData?.id && groupData?.id) {
      // Try to get cached sentences from cacheManager
      const cachedSentences = cacheUtils.getCachedSentences(moduleData.id, groupData.id, 'mcq');

      if (cachedSentences && Array.isArray(cachedSentences)) {
        // Generate choices for the cached sentences
        const generatedChoices = cachedSentences.map(sentence => getMCQChoices(sentence, words));
        setSentences(cachedSentences);
        setChoices(generatedChoices);
        setUserChoices(Array(cachedSentences.length).fill(null));
        setFeedback(Array(cachedSentences.length).fill(null));
        setShowHints(Array(cachedSentences.length).fill(false));
        setLoading(false);
        hasFetched.current = true;
      } else {
        // If no valid cache, fetch and cache new sentences
        hasFetched.current = true;
        fetchSentences();
      }
    }
  }, [words, fetchSentences, moduleData?.id, groupData?.id]);

  const handleChoice = (qIdx, choiceIdx) => {
    if (isSubmitted) return;
    const newChoices = [...userChoices];
    newChoices[qIdx] = (userChoices[qIdx] === choiceIdx) ? null : choiceIdx;
    setUserChoices(newChoices);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sentences || !Array.isArray(sentences)) {
      console.error('Sentences not available for submission');
      return;
    }
    const newFeedback = sentences.map((sentence, idx) => {
      const correct = sentence.correct_word;
      const userChoiceIdx = userChoices[idx];
      if (userChoiceIdx === null) return 'incorrect';
      const isCorrect = choices[idx][userChoiceIdx] === correct;
      return isCorrect ? 'correct' : 'incorrect';
    });
    setFeedback(newFeedback);
    setIsSubmitted(true);

    // Clear the cache for this specific practice type after grading
    if (moduleData?.id && groupData?.id) {
      cacheUtils.clearSentencesCache(moduleData.id, groupData.id, 'mcq');
    }
  };

  const handlePracticeAgain = () => {
    // Clear the cache for this specific practice type
    cacheUtils.clearSentencesCache(moduleData.id, groupData.id, 'mcq');
    hasFetched.current = false;
    fetchSentences();
    setIsSubmitted(false);
  };

  if (dataLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '70vh',
        width: '100%',
        flexDirection: 'column',
      }}>
        <Spinner message={t('loading')} size="large" />
      </div>
    );
  }

  if (!moduleData || !groupData) return (
    <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
      <h2>{t('wordGroupNotFound')}</h2>
      <p>{t('wordGroupNotFoundMessage')}</p>
      <button className="btn btn-primary" onClick={() => navigate(-1)}>{t('backToModules')}</button>
    </div>
  );

  if (words && words.length === 0) return (
    <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
      <h2>{t('noWordsYet')}</h2>
      <p>{t('addYourFirstWords')}</p>
      <button className="btn btn-primary" onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)}>{t('backToWordGroup')}</button>
    </div>
  );

  return (
    <div className="sentence-practice-container">
      <button onClick={() => navigate(`/module/${moduleData.id}/group/${groupData.id}`)} className="btn btn-secondary btn-back">
        {isRTL ? '→' : '←'} {t('backToWordGroup')}
      </button>
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          width: '100%',
          flexDirection: 'column',
        }}>
          <Spinner message={t('generatingSentences')} size="large" rtl={isRTLanguage(getKeyboardLangCode(moduleData.to_lang))} />
        </div>
      ) : error ? (
        <div className="card error-message">
          <h2>{t('error')}</h2>
          <p>{error}</p>
          {error.includes('Want to learn more?') && (
            <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
              <Link
                to="/store"
                className="btn btn-primary"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                {t('upgradeToPremium')}
              </Link>
            </div>
          )}
          <button onClick={handlePracticeAgain} className="btn btn-primary" style={{ marginTop: '1rem' }}>{t('tryAgain')}</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <ol className="sentence-list">
            {(sentences || []).map((sentence, idx) => {
              const sentenceChoices = choices[idx] || [];
              // Determine feedback status for this question
              let status = '';
              if (isSubmitted) {
                if (userChoices[idx] === null || feedback[idx] === 'incorrect') {
                  status = 'answered incorrect';
                } else if (feedback[idx] === 'correct') {
                  status = 'answered correct';
                }
              }

              // RTL logic (copied from fill-in-the-blank)
              const targetLangCode = getKeyboardLangCode(moduleData.to_lang);
              const sourceLangCode = getKeyboardLangCode(moduleData.from_lang);
              const isTargetRTL = isRTLanguage(targetLangCode);
              const isSourceRTL = isRTLanguage(sourceLangCode);
              const convertToRTLPunctuation = (text) => {
                if (!isTargetRTL) return text;
                const converted = text
                  .replace(/!/g, '\u061C!')  // RTL exclamation
                  .replace(/\?/g, '\u061C?')  // RTL question mark
                  .replace(/\./g, '\u061C.')  // RTL period
                  .replace(/,/g, '\u061C,')   // RTL comma
                  .replace(/:/g, '\u061C:/')   // RTL colon
                  .replace(/;/g, '\u061C;/');  // RTL semicolon
                return converted;
              };

              return (
                <li key={idx} className={`sentence-item${status ? ' ' + status : ''}`}
                  style={{ listStyle: 'none', marginBottom: '2rem' }}>
                  <div className="item-content card" style={{ maxWidth: 600, margin: '0 auto', padding: '2rem', textAlign: isTargetRTL ? 'right' : 'center', direction: isTargetRTL ? 'rtl' : 'ltr' }}>
                    {/* Show Hint box */}
                    {!showHints[idx] && (
                      <div
                        style={{
                          background: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px',
                          padding: '0 1.1rem',
                          marginBottom: '1rem',
                          display: 'inline-block',
                          textAlign: isSourceRTL ? 'right' : 'left',
                          lineHeight: 0.95,
                          transition: 'background 0.18s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f3f3f3'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                      >
                        <button
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            fontSize: '0.97rem',
                            padding: 0,
                            margin: 0,
                            fontWeight: 400,
                            cursor: 'pointer',
                            outline: 'none',
                          }}
                          onClick={() => {
                            const newShowHints = [...showHints];
                            newShowHints[idx] = true;
                            setShowHints(newShowHints);
                          }}
                        >
                          {t('showHint')}
                        </button>
                      </div>
                    )}
                    {showHints[idx] && (
                      <p className="translation-hint" style={{ direction: isSourceRTL ? 'rtl' : 'ltr', textAlign: isSourceRTL ? 'right' : 'center' }}>
                        {t('hint')}: "{sentence.translation}"
                      </p>
                    )}
                    {(() => {
                      const parts = sentence.sentence_with_blank.split('____');
                      const beforeText = parts[0];
                      const afterText = parts[1] || '';
                      return (
                        <label className="sentence-text" style={{ display: 'block', fontSize: '1.1rem', marginBottom: '1.2rem', direction: isTargetRTL ? 'rtl' : 'ltr', textAlign: isTargetRTL ? 'right' : 'center' }}>
                          {idx + 1}. {beforeText}
                          <span className="mcq-blank" />
                          {convertToRTLPunctuation(afterText)}
                        </label>
                      );
                    })()}
                    <div className="mcq-choices" style={{
                      direction: isTargetRTL ? 'rtl' : 'ltr'
                    }}>
                      {sentenceChoices.map((choice, cIdx) => {
                        // Determine button classes
                        let btnClass = 'mcq-choice-btn';
                        if (!isSubmitted) {
                          if (userChoices[idx] === cIdx) btnClass += ' selected';
                        } else {
                          if (choice === sentence.correct_word) btnClass += ' correct';
                          else if (userChoices[idx] === cIdx && choice !== sentence.correct_word) btnClass += ' incorrect';
                        }
                        return (
                          <button
                            type="button"
                            key={cIdx}
                            className={btnClass}
                            onClick={() => handleChoice(idx, cIdx)}
                            disabled={isSubmitted}
                            style={{
                              width: 180,
                              height: 48,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1rem',
                              borderRadius: 8,
                              textAlign: 'center',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              padding: 0,
                              transition: 'all 0.2s ease',
                              direction: isTargetRTL ? 'rtl' : 'ltr',
                            }}
                          >
                            {choice}
                          </button>
                        );
                      })}
                    </div>
                    {isSubmitted && (
                      <div className={`feedback-text ${feedback[idx]}`} style={{ marginTop: '1rem', fontWeight: 500, direction: isSourceRTL ? 'rtl' : 'ltr', textAlign: isSourceRTL ? 'right' : 'center' }}>
                        {feedback[idx] === 'correct'
                          ? t('correct')
                          : `${t('incorrect')} ${sentence.correct_word}`}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="practice-actions">
            {!isSubmitted ? (
              <button type="submit" className="btn btn-primary">
                {t('checkAnswers')}
              </button>
            ) : (
              <button type="button" className="btn btn-primary" onClick={handlePracticeAgain}>
                {t('practiceAgainWithNewSentences')}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default MultipleChoicePracticePage; 